import { useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { Dialogo } from './piezas.js';
import {
  base64DeDataUrl,
  compartirArchivoNativo,
  descargarBase64,
  nombreArchivoQr,
  pdfQrDesde,
  type ActivoEtiquetaQr,
} from './etiquetas-qr.js';

/**
 * QR para el celular. Muestra la imagen y ofrece guardarla/compartirla y bajar un PDF
 * para imprimir. En la app instalada abre la hoja de compartir del sistema; en el
 * navegador del celular descarga el archivo.
 */
export function VisorQr({
  imagen,
  etiquetas,
  titulo,
  activos,
  alCerrar,
}: {
  /** Hoja con todas las etiquetas, para mostrar. */
  imagen: string;
  /** Una imagen por activo, ya generada: de ellas salen el PDF sin volver a dibujarlas. */
  etiquetas: string[];
  titulo: string;
  activos: ActivoEtiquetaQr[];
  alCerrar: () => void;
}): JSX.Element {
  const [ocupado, setOcupado] = useState<'imagen' | 'pdf' | null>(null);
  const [aviso, setAviso] = useState<{ tono: 'info' | 'error'; texto: string } | null>(null);

  async function entregar(tipo: 'imagen' | 'pdf'): Promise<void> {
    setAviso(null);
    setOcupado(tipo);
    try {
      const extension = tipo === 'pdf' ? 'pdf' : 'png';
      const mime = tipo === 'pdf' ? 'application/pdf' : 'image/png';
      const nombre = nombreArchivoQr(activos, extension);
      const base64 = tipo === 'pdf' ? await pdfQrDesde(etiquetas) : base64DeDataUrl(imagen);
      if (Capacitor.isNativePlatform()) {
        await compartirArchivoNativo(nombre, base64);
      } else {
        descargarBase64(nombre, base64, mime);
        setAviso({
          tono: 'info',
          texto:
            tipo === 'pdf'
              ? 'Si no se descargó, revise las descargas del navegador.'
              : 'Si no se guardó, mantenga presionada la imagen y elija «Guardar imagen».',
        });
      }
    } catch (error) {
      setAviso({
        tono: 'error',
        texto: error instanceof Error ? error.message : 'No se pudo preparar el archivo. Intente de nuevo.',
      });
    } finally {
      setOcupado(null);
    }
  }

  return (
    <Dialogo titulo={titulo} ancho={560} alCerrar={alCerrar}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
        <button
          type="button"
          className="boton"
          aria-busy={ocupado === 'imagen'}
          disabled={ocupado !== null}
          onClick={() => void entregar('imagen')}
        >
          {ocupado === 'imagen' ? 'Preparando…' : 'Guardar o compartir imagen'}
        </button>
        <button
          type="button"
          className="boton secundario"
          aria-busy={ocupado === 'pdf'}
          disabled={ocupado !== null}
          onClick={() => void entregar('pdf')}
        >
          {ocupado === 'pdf' ? 'Preparando…' : 'Descargar PDF / imprimir'}
        </button>
      </div>
      <div role="status" aria-live="polite">
        {aviso ? (
          <p className="campo-nota" style={aviso.tono === 'error' ? { color: 'var(--peligro, #b00020)' } : undefined}>
            {aviso.texto}
          </p>
        ) : null}
      </div>
      <img
        src={imagen}
        alt={titulo}
        style={{ width: '100%', height: 'auto', borderRadius: 8, background: '#fff', display: 'block' }}
      />
    </Dialogo>
  );
}
